import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import AnimatedSection from '../components/AnimatedSection';
import './FAQ.css';

const faqCategories = [
  {
    name: 'General',
    faqs: [
      { q: 'What is Sculpit?', a: 'Sculpit is India\'s first AI-powered end-to-end residential construction platform. We help homeowners design their home in 3D, get instant cost estimates, connect with verified builders, and track the entire construction process - all from one place.' },
      { q: 'Who is Sculpit for?', a: 'Sculpit is for anyone planning to build a house in India - whether you\'re a first-time home builder, someone renovating, or a family that owns a plot and wants to construct their dream home. We also serve builders and architects through our Business plans.' },
      { q: 'Which cities does Sculpit serve?', a: 'Currently, we serve homeowners building in Bangalore, Chennai, Hyderabad, and Pune. We\'re rapidly expanding to more cities across India.' },
      { q: 'Is Sculpit free to use?', a: 'Yes! You can sign up for free and get 20 credits to design your first home in 3D, get a cost breakdown, and even book a free consultation. Our paid plans start at ₹2,999/month for serious home builders.' },
      { q: 'How is Sculpit different from hiring an architect and contractor separately?', a: 'Sculpit combines AI-powered design, real-time cost estimation, verified construction partners, escrow-protected payments, and project tracking into one seamless platform. Instead of managing 5-6 different parties, you get everything through one platform with full transparency.' },
    ],
  },
  {
    name: 'Design & Planning',
    faqs: [
      { q: 'How does the 3D design studio work?', a: 'Our AI-powered studio lets you design your home by dragging and dropping rooms onto your plot. You set wall dimensions, place doors and windows, add furniture, and see everything in real-time 3D. You can also walk through your design virtually.' },
      { q: 'Can I upload my existing blueprint?', a: 'Absolutely! Our AI can convert your existing 2D blueprint or floor plan into an interactive 3D model. You can then modify it, see cost estimates, and make any changes before construction starts.' },
      { q: 'What is Vastu compliance checking?', a: 'Our built-in Vastu checker analyzes your home design against traditional Vastu Shastra principles - room directions, entrance placement, kitchen positioning, etc. - and gives you a compliance score with suggestions for improvement.' },
      { q: 'What is MEP planning and clash detection?', a: 'MEP stands for Mechanical, Electrical, and Plumbing. Our AI plans these systems and automatically detects conflicts (like a pipe running through a beam) before construction starts, preventing expensive rework later.' },
      { q: 'Can I design multiple versions of my home?', a: 'Yes. Free users can create 2-3 designs. Pro users get unlimited designs and can compare them side by side, making it easy to pick the best version.' },
    ],
  },
  {
    name: 'Construction & Partners',
    faqs: [
      { q: 'How are construction partners vetted?', a: 'All Sculpit partners must be RERA registered, have 10+ years of operation, ISO quality certification (preferred), at least 50 completed residential projects, clean legal records, and must agree to Sculpit\'s quality standards including 15+ mandatory inspections per project.' },
      { q: 'Who is responsible for construction quality?', a: 'Sculpit takes responsibility. We mandate 15+ quality inspections at every critical stage (pre-concrete, structural, MEP, waterproofing, finishes). Our quality team independently verifies each milestone before releasing escrow payments.' },
      { q: 'What happens if there\'s a delay?', a: 'Our predictive system alerts you 3-5 days before potential delays. If delays occur, Sculpit mediates between you and the construction partner to find solutions. Persistent delays trigger contractual penalties on the builder.' },
      { q: 'Can I choose my own contractor?', a: 'Sculpit\'s strength is in auto-matching you with the best verified partner. However, if you have a preferred builder, they can apply to join the Sculpit network and go through our verification process.' },
    ],
  },
  {
    name: 'Payments & Pricing',
    faqs: [
      { q: 'How does escrow-protected payment work?', a: 'Your construction payments go into a Sculpit escrow account - a secure holding account managed by a regulated financial partner. Money is released to the builder only after each construction milestone is independently verified through quality inspections.' },
      { q: 'What do credits cost?', a: 'Free plan includes 20 credits. Pro plan (₹2,999/mo) includes 300 credits. Additional credit packs start at ₹499 for 50 credits. Different features use different amounts of credits (e.g., 2D plan = 2 credits, 3D view = 5 credits).' },
      { q: 'Can I cancel my subscription anytime?', a: 'Monthly plans can be cancelled anytime - no penalties. Annual plans come with a 30-day full refund policy. Unused credits carry forward for 90 days after cancellation.' },
      { q: 'Are there any hidden fees?', a: 'None. Our pricing is fully transparent. Platform subscription, credit costs, and construction pricing are all clearly stated upfront. The cost estimate you see in the platform is the cost you pay.' },
    ],
  },
  {
    name: 'Home Loans',
    faqs: [
      { q: 'Which banks does Sculpit work with?', a: 'We partner with major banks including SBI, HDFC, ICICI, Axis Bank, and several others for construction home loans. Our pre-verified builder credentials help speed up the approval process.' },
      { q: 'What documents are needed for a home loan?', a: 'Typically: identity proof, address proof, income proof (salary slips/ITR), property documents, building plan approvals, and construction cost estimate. Sculpit provides lender-ready construction documentation automatically.' },
      { q: 'How does Sculpit speed up loan approval?', a: 'Banks trust Sculpit projects because we provide: verified builder credentials, detailed cost breakdowns, milestone-based payment schedules, and escrow-protected payment infrastructure - all reducing the bank\'s risk assessment time.' },
    ],
  },
];

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'faq-item--open' : ''}`}>
      <button className="faq-item__q" onClick={() => setOpen(!open)}>
        <span>{question}</span>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {open && <div className="faq-item__a"><p>{answer}</p></div>}
    </div>
  );
}

export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState('General');
  const currentFAQs = faqCategories.find(c => c.name === activeCategory)?.faqs || [];

  return (
    <div className="faq-page">
      <section className="faq-hero section-dark">
        <div className="faq-hero__bg"><div className="faq-hero__orb" /></div>
        <div className="container faq-hero__content">
          <AnimatedSection>
            <span className="label label-teal">Help Center</span>
            <h1>Frequently Asked <span className="text-gradient">Questions</span></h1>
            <p className="faq-hero__subtitle">
              Everything you need to know about Sculpit. Can't find the answer? Chat with us on WhatsApp.
            </p>
          </AnimatedSection>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="faq-layout">
            <div className="faq-sidebar">
              {faqCategories.map(cat => (
                <button
                  key={cat.name}
                  className={`faq-sidebar__btn ${activeCategory === cat.name ? 'faq-sidebar__btn--active' : ''}`}
                  onClick={() => setActiveCategory(cat.name)}
                >
                  {cat.name}
                  <span className="faq-sidebar__count">{cat.faqs.length}</span>
                </button>
              ))}
            </div>

            <div className="faq-content">
              <h2 style={{ marginBottom: '24px' }}>{activeCategory}</h2>
              <div className="faq-list">
                {currentFAQs.map((faq, i) => (
                  <FAQItem key={i} question={faq.q} answer={faq.a} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-gray" style={{ textAlign: 'center' }}>
        <div className="container">
          <AnimatedSection>
            <h2>Still Have Questions?</h2>
            <p className="cta-subtitle">
              Chat with us on WhatsApp - we typically reply within 5 minutes.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
                <FaWhatsapp /> Chat on WhatsApp
              </a>
              <Link to="/contact" className="btn btn-secondary btn-lg">
                Contact Us <ArrowRight size={18} />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
